import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Input, Radio, Select, Space, message } from 'antd';
import { daysTH, monthsTH } from '../../utils/util';
import { filterOption, filterOptionSort } from '../../utils/select';
import { getDateFromDatePicker } from '../../utils/datepicker';
import DatePicker, { DateObject } from "react-multi-date-picker";
import InputIcon from 'react-multi-date-picker/components/input_icon';
import { product, productType, unitType, vat, vendor } from '../../model/master';
import CommonDetail from '../common/common-detail';
import CommonDataTable from '../common/common-data-table';
import { api_product_type, api_product_unit } from '../../api/product';
import { api_vendor } from '../../api/vendor';

interface props {
    product: product,
    onChange: React.Dispatch<React.SetStateAction<object>>
}

const { Option } = Select;

const ProductDetail: React.FC<props> = (prop) => {

    const months = monthsTH();
    const days = daysTH();
    const [data, setData] = useState<any>({});
    const [openTierPrice, setOpenTierPrice] = useState(false);

    const [code, setCode] = useState('');
    const [name, setName] = useState('');
    const [nameOther, setNameOther] = useState('');
    const [type, setType] = useState(0);
    const [unit, setUnit] = useState(0);
    const [quantity, setQuantity] = useState(1);
    const [normalType, setNormalType] = useState(1);
    const [cost, setCost] = useState('');
    const [priceRetail, setPriceRetail] = useState('');
    const [priceWholesale, setPriceWholesale] = useState('');
    const [priceRetailMember, setPriceRetailMember] = useState('');
    const [priceWholesaleMember, setPriceWholesaleMember] = useState('');
    const [initOrderAmount, setInitOrderAmount] = useState('');
    const [pointPurchase, setPointPurchase] = useState('');
    const [limitQuantity, setLimitQuantity] = useState('');

    /**
     * Settings that contradict each other, named before they are saved.
     *
     * Found on Staging while testing the purchase-order cap: a product with
     * จุดสั่งซื้อ 150 and จำกัดจำนวน 100 sits permanently in "ใกล้หมด" and can
     * only ever be topped up by the couple of units left under the ceiling.
     * The system behaves correctly and the shop cannot tell why the orders
     * are tiny, which is the worst combination.
     *
     * A warning, not a block: these are legitimate numbers in some trade
     * -- a wholesaler might genuinely cap what it holds below the level it
     * reorders at -- and the shop knows its own business better than a
     * validation rule does.
     */
    const asNumber = (v: string | number): number | null => {
        if (v === null || v === undefined || String(v).trim() === '') return null;
        const n = Number(v);
        return isNaN(n) ? null : n;
    };

    const settingConflicts = (): string[] => {
        const point = asNumber(pointPurchase);
        const cap = asNumber(limitQuantity);
        const initOrder = asNumber(initOrderAmount);
        const warnings: string[] = [];

        if (point !== null && cap !== null && point > cap) {
            warnings.push(
                `จุดสั่งซื้อ (${point}) สูงกว่าจำกัดจำนวน (${cap}) — สินค้าจะขึ้นว่า "ใกล้หมด" ตลอดเวลา ` +
                `และสั่งเพิ่มได้ครั้งละไม่เกิน ${cap} − คงเหลือ เท่านั้น`
            );
        }
        if (initOrder !== null && cap !== null && initOrder > cap) {
            warnings.push(
                `จำนวนสั่งซื้อเริ่มต้น (${initOrder}) มากกว่าจำกัดจำนวน (${cap}) — ` +
                `ระบบจะลดจำนวนที่สั่งลงให้ไม่เกินเพดานทุกครั้ง`
            );
        }
        return warnings;
    };
    const [vat, setVat] = useState(-1);
    const [point, setPoint] = useState('');
    const [isCutStock, setIsCutStock] = useState(false);
    const [isTierPrice, setIsTierPrice] = useState(false);
    const [isAvgCost, setIsAvgCost] = useState(false);
    const [expireDate, setExpireDate] = useState<DateObject>(new DateObject());
    const [vendor, setVendor] = useState<any>([]);
    const [remark, setRemark] = useState('');

    const [hasBarcode, setHasBarcode] = useState(true);
    const isAddMode = !data.id || data.id === 0;

    const [typeList, setTypeList] = useState<productType[]>([]);
    const [unitList, setUnitList] = useState<unitType[]>([]);
    const [vatList, setVatList] = useState<vat[]>([]);
    const [vendorList, setVendorList] = useState<vendor[]>([]);

    const [tierPriceData, setTierPriceData] = useState([]);

    const init = async () => {
        setVatList([
            { key: 0, id: -1, name: '-' },
            { key: 1, id: 0, name: 'Vat out' },
            { key: 2, id: 1, name: 'Vat in' }
        ])

        // Each list is fetched on its own: one failing dropdown must not
        // leave the other two empty, which is what a single sequential
        // await chain did as soon as these APIs stopped swallowing errors.
        const load = async <T,>(what: string, fetch: () => Promise<any>, set: (v: T[]) => void) => {
            try {
                const res = await fetch();
                set(Array.isArray(res) ? res : []);
            } catch (e: any) {
                set([]);
                message.error(e?.response?.data?.message || e?.message || `โหลด${what}ไม่สำเร็จ`);
            }
        };
        await load('ประเภทสินค้า', () => api_product_type('LIST', {}), setTypeList);
        await load('หน่วยสินค้า', () => api_product_unit('LIST', {}), setUnitList);
        await load('ข้อมูลผู้ขายส่ง', () => api_vendor('LIST', {}), setVendorList);
    }

    const setInputData = (object: any) => {
        setCode(object.product_code);
        setName(object.product_name);
        setNameOther(object.product_name_other);
        setType(object.product_type);
        setUnit(object.product_unit);
        setQuantity(object.quantity)
        setNormalType(object.product_normal_type);
        setCost(object.product_cost);
        setPriceRetail(object.price_retail);
        setPriceWholesale(object.price_wholesale);
        setPriceRetailMember(object.price_retail_member);
        setPriceWholesaleMember(object.price_wholesale_member);
        setInitOrderAmount(object.init_order_amount);
        setPointPurchase(object.point_purchase);
        setLimitQuantity(object.limit_quantity);
        setVat(object.vat);
        setPoint(object.point);
        setIsCutStock(object.is_cut_stock);
        setIsTierPrice(object.is_tier_price);
        setIsAvgCost(object.is_avg_cost);
        setExpireDate(new DateObject(object.expire_date));
        setVendor(object.vendor);
        setRemark(object.remark);
        setTierPriceData(object.tier_price != null ? JSON.parse(object.tier_price) : []);
    }

    const onDataChange = (e: any) => {
        let key: string = String(e.target.name);
        let value: any = (
            e.target.type === 'checkbox'
                ? (e.target.checked ? 1 : 0)
                : e.target.type === 'radio' ? (e.target.value ? 1 : 0)
                : typeof(e.target.value) === 'number' ? Number(e.target.value)
                : String(e.target.value)
        );
        let object: any = data;
        object[key] = value;

        setData(object);
        setInputData(object);
        prop.onChange(object);
    }
    
    const onTierPriceDataChange = (e: any) => {
        let object: any = {...data};
        object['tier_price'] = JSON.stringify(e);

        setData(object);
        setInputData(object);
        prop.onChange(object);
    }

    const onMappingDataBeforeChange = (e: any, name: string) => {
        let object = {
            target: {
                name: name,
                value: typeof(e) == 'number' ? e : getDateFromDatePicker(e)
            }
        }
        onDataChange(object);
    }

    const onOpenTierPrice = () => {
        setOpenTierPrice(true);
    }

    const onCloseTierPrice = () => {
        setOpenTierPrice(false);
    };

    const onSaveTierPrice = () => {
        setOpenTierPrice(false);
    }

    const onVendorChange = (value: string[]) => {
        let object: any = data;
        object['vendor'] = value;

        setData(object);
        setInputData(object);
        prop.onChange(object);
    }

    const tierPriceColumn: any = [
        { title: 'ขั้นต่ำ', dataIndex: 'min', key: 'min', type: 'INPUT', width: '100px', edit: true },
        { title: 'สูงสุด', dataIndex: 'max', key: 'max', type: 'INPUT', width: '100px', edit: true },
        { title: 'ราคา', dataIndex: 'price', key: 'price', type: 'INPUT', width: '100px', edit: true },
        { title: 'ประเภท', dataIndex: 'price_type', key: 'price_type', type: 'SELECT', width: '100px', edit: true, list: [{ "id": 1, "name": "ประเภท 1" }, { "id": 2, "name": "ประเภท 2" }] },
        { title: 'กลุ่ม', dataIndex: 'member_group', key: 'member_group', type: 'SELECT', width: '100px', edit: true, list: [] }
    ]

    useEffect(() => {
        init();
        setData(prop.product);
        setInputData(prop.product);
    }, []);


    return (
        <>
            <div className='detailContainer'>
                <div className='detailBox'>
                    <div className='InputBox'>
                        <div className='formInput'>
                            <label></label>
                            <div className='formInputRadio'>
                                <Radio.Group name="product_normal_type" value={normalType} onChange={onDataChange}>
                                    <Radio value={1}>สินค้าทั่วไป</Radio>
                                    <Radio value={0}>สินค้าชั่งน้ำหนัก</Radio>
                                </Radio.Group>
                            </div>
                        </div>
                        {isAddMode && (
                            <div className='formInput'>
                                <label>รูปแบบบาร์โค้ด</label>
                                <div className='formInputRadio'>
                                    <Radio.Group
                                        value={hasBarcode ? 1 : 0}
                                        onChange={(e) => {
                                            const withBarcode = e.target.value === 1;
                                            setHasBarcode(withBarcode);
                                            const newObj = { ...data, has_barcode: withBarcode, product_code: withBarcode ? code : '' };
                                            setData(newObj);
                                            prop.onChange(newObj);
                                        }}
                                        data-testid="radio-barcode-mode"
                                    >
                                        <Radio value={1}>🏷️ มีบาร์โค้ดจากผู้ผลิต</Radio>
                                        <Radio value={0}>⚡ ไม่มีบาร์โค้ด (ระบบสร้างรหัส SKU อัตโนมัติ)</Radio>
                                    </Radio.Group>
                                </div>
                            </div>
                        )}
                        <div className='formInput'>
                            <label>รหัสสินค้า/บาร์โค้ด{hasBarcode ? <span> *</span> : ''}</label>
                            {isAddMode && !hasBarcode ? (
                                <div style={{
                                    padding: '8px 14px',
                                    background: 'rgba(255, 122, 89, 0.1)',
                                    border: '1px dashed var(--primary, #FF7A59)',
                                    borderRadius: '10px',
                                    color: 'var(--primary, #FF7A59)',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    width: '100%'
                                }} data-testid="auto-sku-notice">
                                    ⚡ ระบบจะสร้างรหัสสินค้าภายใน (เช่น SKU000001) ให้อัตโนมัติเมื่อกดบันทึก
                                </div>
                            ) : isAddMode ? (
                                <Input
                                    name="product_code"
                                    value={code}
                                    placeholder="กรอกหรือสแกนบาร์โค้ดสินค้า..."
                                    onChange={onDataChange}
                                    data-testid="input-product-code"
                                />
                            ) : (
                                <Input
                                    name="product_code"
                                    value={code}
                                    disabled
                                    style={{ fontWeight: 600 }}
                                    data-testid="display-product-code"
                                />
                            )}
                        </div>
                        <div className='formInput'>
                            <label>ชื่อสินค้า<span> *</span></label>
                            <Input name="product_name" value={name} placeholder={'กรุณาป้อนชื่อสินค้า'} onChange={onDataChange} />
                        </div>
                        <div className='formInput'>
                            <label>ชื่อสินค้าอื่นๆ</label>
                            <Input name="product_name_other" value={nameOther} placeholder={'กรุณาป้อนชื่อสินค้า'} onChange={onDataChange} />
                        </div>
                        <div className='formInput'>
                            <label>ประเภท<span> *</span></label>
                            <Select value={type} showSearch filterOption={filterOption} onChange={(e) => { onMappingDataBeforeChange(e, 'product_type') }} >
                                <Option key={0} value={0} code={""} disabled>กรุณาเลือกประเภท</Option>
                                {
                                    (typeList || []).map(obj => {
                                        return <Option key={obj.id} value={obj.id}>{obj.name}</Option>
                                    })
                                }
                            </Select>
                        </div>
                        <div className='formInput'>
                            <label>หน่วย<span> *</span></label>
                            <Select value={unit} showSearch filterOption={filterOption} onChange={(e) => { onMappingDataBeforeChange(e, 'product_unit') }} >
                                <Option key={0} value={0} code={""} disabled>กรุณาเลือกหน่วย</Option>
                                {
                                    (unitList || []).map(obj => {
                                        return <Option key={obj.id} value={obj.id}>{obj.name}</Option>
                                    })
                                }
                            </Select>
                        </div>
                        <div className='formInput'>
                            <label>จำนวนสินค้า</label>
                            <Input name="quantity" value={quantity} placeholder={'กรุณาป้อนจำนวนสินค้า'} onChange={onDataChange} disabled={data.id > 0} />
                        </div>
                        <div className='formInput'>
                            <label>ราคาต้นทุน<span> *</span></label>
                            <Input name="product_cost" value={cost} placeholder={'กรุณาป้อนราคาต้นทุน'} onChange={onDataChange} />
                        </div>
                        <div className='formInput'>
                            <label>ราคาขายปลีก<span> *</span></label>
                            <Input name="price_retail" value={priceRetail} placeholder={'กรุณาป้อนราคาขายปลีก'} onChange={onDataChange} />
                        </div>
                        <div className='formInput'>
                            <label>ราคาขายส่ง</label>
                            <Input name="price_wholesale" value={priceWholesale} placeholder={'กรุณาป้อนราคาขายส่ง'} onChange={onDataChange} />
                        </div>
                        <div className='formInput'>
                            <label>ราคาขายปลีกสมาชิก</label>
                            <Input name="price_retail_member" value={priceRetailMember} placeholder={'กรุณาป้อนราคาขายปลีกสมาชิก'} onChange={onDataChange} />
                        </div>
                        <div className='formInput'>
                            <label>ราคาขายส่งสมาชิก</label>
                            <Input name="price_wholesale_member" value={priceWholesaleMember} placeholder={'กรุณาป้อนราคาขายส่งสมาชิก'} onChange={onDataChange} />
                        </div>
                        <div className='formInput'>
                            <label>จำนวนสั่งซื้อเริ่มต้น</label>
                            <Input name="init_order_amount" value={initOrderAmount} placeholder={'จำนวนที่จะสั่งเมื่อถึงจุดสั่งซื้อ'} onChange={onDataChange} />
                        </div>
                        <div className='formInput'>
                            <label>จุดสั่งซื้อ</label>
                            <Input name="point_purchase" value={pointPurchase} placeholder={'เตือนเมื่อเหลือถึงจำนวนนี้ (เว้นว่าง = ไม่เตือน)'} onChange={onDataChange} />
                        </div>
                        <div className='formInput'>
                            <label>จำกัดจำนวน</label>
                            <Input name="limit_quantity" value={limitQuantity} placeholder={'จำนวนสูงสุดที่ควรมีในสต๊อก (เว้นว่าง = ไม่จำกัด)'} onChange={onDataChange} />
                        </div>

                        {settingConflicts().length > 0 && (
                            <div className='product-setting-warning' data-testid='product-setting-warning'>
                                <span aria-hidden='true'>⚠️</span>
                                <div>
                                    {settingConflicts().map((w, i) => <div key={i}>{w}</div>)}
                                </div>
                            </div>
                        )}

                        <div className='formInput'>
                            <label>Vat</label>
                            <Select value={vat} showSearch filterOption={filterOption} onChange={(e) => { onMappingDataBeforeChange(e, 'vat') }} >
                                {
                                    vatList.map(obj => {
                                        return <Option key={obj.id} value={obj.id}>{obj.name}</Option>
                                    })
                                }
                            </Select>
                        </div>
                        <div className='formInput'>
                            <label>แต้มสินค้า</label>
                            <Input name="point" value={point} placeholder={'กรุณาป้อนแต้มสินค้า'} onChange={onDataChange} />
                        </div>
                        <div className='formInput'>
                            <label>วันหมดอายุ</label>
                            <div className='formInputDate'>
                                <DatePicker
                                    className="fromInputDatetime"
                                    months={months}
                                    weekDays={days}
                                    value={expireDate}
                                    onChange={(e) => { onMappingDataBeforeChange(e, 'expire_date') }}
                                    render={<InputIcon/>}
                                    format={"DD/MM/YYYY"}
                                />
                            </div>
                        </div>
                        <div className='formInput'>
                            <label>ผู้ขาย</label>
                            <Select 
                                mode="multiple"
                                allowClear
                                value={vendor} showSearch filterOption={filterOption} filterSort={filterOptionSort} onChange={onVendorChange} >
                                {/* value={vendor} showSearch filterOption={filterOption} filterSort={filterOptionSort} onChange={(e) => { onMappingDataBeforeChange(e, 'vendor') }} > */}
                                {/* <Option key={0} value={0} code={""} disabled>กรุณาเลือกผู้ขาย</Option> */}
                                {
                                    vendorList.map(obj => {
                                        return <Option key={obj.id} value={obj.id}>{obj.name}</Option>
                                    })
                                }
                            </Select>
                        </div>
                        <div className='formInput'>
                            <label>หมายเหตุ</label>
                            <Input name="remark" value={remark} placeholder={'กรุณาป้อนหมายเหตุ'} onChange={onDataChange} />
                        </div>
                        <div className='formInput'>
                            <label></label>
                            <div className='formInputCheckbox'>
                                <Checkbox name='is_cut_stock' checked={isCutStock} onChange={onDataChange}>ไม่ตัดสต๊อก</Checkbox>
                            </div>
                        </div>
                        <div className='formInput'>
                            <label></label>
                            <div className='formInputCheckbox'>
                                <Checkbox name='is_avg_cost' checked={isAvgCost} onChange={onDataChange}>คำนวนต้นทุนแบบเฉลี่ย</Checkbox>
                            </div>
                        </div>
                        <div className='formInput'>
                            <label></label>
                            <div className='formInputCheckbox'>
                                <Checkbox name='is_tier_price' checked={isTierPrice} onChange={onDataChange}>ราคาหลายระดับ</Checkbox>
                                { isTierPrice ? <Button className='btn-common' onClick={onOpenTierPrice}>ตั้งค่าราคาหลายระดับ</Button> : null }
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <CommonDetail width='40%' mode="VIEW_ONLY" title="ตั้งค่าราคาหลายระดับ" open={openTierPrice} onClosed={onCloseTierPrice} onSaved={onSaveTierPrice} 
                element={ <CommonDataTable columns={tierPriceColumn} data={[...tierPriceData]} operation='IN_TABLE' scollX={768} onChange={onTierPriceDataChange} />} 
            />
        </>
    );
}

export default ProductDetail;